import type {
	IAuthenticationService,
	ITokenService,
	IUserStorageService,
	AuthenticationResult,
} from "@/application/ports/services";
import type { IMonitoring } from "@/application/ports/IMonitoring";
import type { LoginCredentials } from "@/domain/types";
import { UserEntity } from "@/domain/user";
import { getUserErrorMessage } from "@/application/errors/ApiClientError";
import { toSentryContext } from "@/application/services/authUtils";

export class LoginUseCase {
	private authService: IAuthenticationService;
	private tokenService: ITokenService;
	private userStorageService: IUserStorageService;
	private monitoring: IMonitoring;

	constructor(
		authService: IAuthenticationService,
		tokenService: ITokenService,
		userStorageService: IUserStorageService,
		monitoring: IMonitoring,
	) {
		this.authService = authService;
		this.tokenService = tokenService;
		this.userStorageService = userStorageService;
		this.monitoring = monitoring;
	}

	async execute(credentials: LoginCredentials): Promise<AuthenticationResult> {
		try {
			// Validate credentials
			if (!credentials.email || !credentials.password) {
				return {
					success: false,
					error: "Email and password are required",
				};
			}

			if (!UserEntity.isValidEmail(credentials.email)) {
				return {
					success: false,
					error: "Please enter a valid email address",
				};
			}

			// Attempt authentication
			const result = await this.authService.login(
				credentials.email,
				credentials.password,
			);

			if (!result.success) {
				return result;
			}

			if (!result.user || !result.token) {
				return {
					success: false,
					error: "Authentication failed - invalid response",
				};
			}

			// Create user entity for business logic validation
			const userEntity = UserEntity.create(result.user);

			// Check if user can login
			if (!userEntity.canLogin()) {
				return {
					success: false,
					error: "Account is not active. Please contact support.",
				};
			}

			// Store authentication data
			this.tokenService.setToken(result.token);
			if (result.refreshToken) {
				this.tokenService.setRefreshToken(result.refreshToken);
			}
			this.userStorageService.setCurrentUser(result.user);

			// Set Sentry user context after successful login
			this.monitoring.setUserContext(toSentryContext(result.user));

			return {
				success: true,
				user: result.user,
				token: result.token,
				refreshToken: result.refreshToken,
				expiresIn: result.expiresIn,
			};
		} catch (error) {
			return {
				success: false,
				error: getUserErrorMessage(error, "Authentication failed"),
			};
		}
	}
}
